import { useAtomValue } from 'jotai'
import { autoLoadMediaAtom } from '@/atoms/preferences'
import { tagNameEquals } from '@/lib/tag'
import { cn } from '@/lib/utils'
import { Event } from '@nostr/tools/wasm'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import ExternalLink from '../ExternalLink'

export const GEOCACHE_LISTING = 37516
export const GEOCACHE_FOUND_LOG = 7516
export const GEOCACHE_VERIFICATION = 7517
export const GEOCACHE_CURATION_LIST = 37517

export default function GeocacheListing({
  event,
  className
}: {
  event: Event
  className?: string
}) {
  const autoLoadMedia = useAtomValue(autoLoadMediaAtom)
  const { t } = useTranslation()
  const name = useMemo(() => event.tags.find(tagNameEquals('name'))?.[1], [event])
  const dTag = useMemo(() => event.tags.find(tagNameEquals('d'))?.[1], [event])
  const hint = useMemo(() => event.tags.find(tagNameEquals('hint'))?.[1], [event])
  const mission = useMemo(() => event.tags.find(tagNameEquals('mission'))?.[1], [event])
  const geohashes = useMemo(
    () =>
      event.tags
        .filter((t) => t[0] === 'g')
        .map((t) => t[1])
        .filter(Boolean),
    [event]
  )
  const difficulty = useMemo(() => event.tags.find(tagNameEquals('D'))?.[1], [event])
  const terrain = useMemo(() => event.tags.find(tagNameEquals('T'))?.[1], [event])
  const size = useMemo(() => event.tags.find(tagNameEquals('S'))?.[1], [event])
  const cacheType = useMemo(
    () => event.tags.find(tagNameEquals('t'))?.[1] ?? 'traditional',
    [event]
  )
  const modifiers = useMemo(
    () =>
      event.tags
        .filter((t) => t[0] === 'n')
        .map((t) => t[1])
        .filter(Boolean),
    [event]
  )
  const images = useMemo(
    () =>
      event.tags
        .filter((t) => t[0] === 'image')
        .map((t) => t[1])
        .filter(Boolean),
    [event]
  )
  const relays = useMemo(
    () =>
      event.tags
        .filter((t) => t[0] === 'r')
        .map((t) => t[1])
        .filter(Boolean),
    [event]
  )
  const verificationPubkey = useMemo(
    () => event.tags.find(tagNameEquals('verification'))?.[1],
    [event]
  )
  const firstToFind = useMemo(() => event.tags.find(tagNameEquals('F'))?.[1], [event])
  const archived = useMemo(
    () => event.tags.some((t) => t[0] === 't' && t[1] === 'archived'),
    [event]
  )

  const detail = (label: string, value?: string) =>
    value && (
      <div>
        <span className="font-medium text-foreground">{label}: </span>
        {value}
      </div>
    )

  return (
    <div className={cn('space-y-2', className)}>
      <div className="flex items-center gap-2">
        <div className="font-medium">{t('Geocache')}</div>
        {archived && (
          <span className="text-xs px-2 py-0.5 rounded bg-muted text-muted-foreground">
            {t('archived')}
          </span>
        )}
      </div>
      <div className="text-xl font-semibold line-clamp-1">{name || dTag}</div>
      {images.length > 0 && autoLoadMedia && (
        <img
          src={images[0]}
          alt={name || dTag}
          className="w-full aspect-video object-cover rounded-lg"
        />
      )}
      {event.content && (
        <div className="prose prose-zinc max-w-none dark:prose-invert break-words">
          {event.content}
        </div>
      )}
      <div className="space-y-1 text-sm text-muted-foreground">
        {detail(t('Type'), cacheType)}
        {detail(t('Difficulty'), difficulty)}
        {detail(t('Terrain'), terrain)}
        {detail(t('Size'), size)}
        {geohashes.length > 0 && detail(t('Location'), geohashes[geohashes.length - 1])}
        {modifiers.length > 0 && detail(t('Modifiers'), modifiers.join(', '))}
        {hint && detail(t('Hint'), hint)}
        {mission && detail(t('Mission'), mission)}
        {verificationPubkey && detail(t('Verification'), verificationPubkey.slice(0, 12) + '…')}
        {firstToFind && detail(t('First to find'), firstToFind.slice(0, 12) + '…')}
        {relays.length > 0 && (
          <div>
            <span className="font-medium text-foreground">{t('Relays')}: </span>
            {relays.map((r, i) => (
              <span key={i}>
                {i > 0 && <span>, </span>}
                <ExternalLink url={r} />
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
